/**
 * Arcing trajectory (indirect fire) for area effects — the shared parabola sampler.
 *
 * An ability carrying the `arcing_trajectory` layer lobs its area in a ballistic arc from the
 * emitter to the landing point instead of firing it down a straight line (owner 2026-09-15:
 * catapult / trebuchet payloads, hurled boulders, thrown flasks, "lob" spells). The shape and its
 * occupant resolution are untouched; the ONLY thing the layer changes is the caster→centre line of
 * effect, which becomes this sampled parabola, and the aim-sight rule, which an arc waives.
 *
 * PHP twin: ft_aoe_arc_samples() in includes/functions/janus/encounter/ft3d_shape_core.php. The
 * constants and the maths MUST stay identical — tools/test/ft3d-aoe-arc-parity.mjs proves it.
 *
 * Geometry (feet, z up):
 *   d = horizontal distance emitter→landing, R = the ability's max range (null/∞ = unlimited)
 *   θ = 90° − 45° × clamp(d / R, 0, 1)   (near-vertical lob at point blank, 45° at max range;
 *                                          an unlimited range always flies the flattest 45° arc)
 *   h = min(d × tan(θ) / 4, ARC_APEX_MAX_FT)   apex height above the straight chord
 *   z(t) = lerp(Sz, Lz, t) + 4·h·t·(1 − t),  x/y lerp linearly.
 */

export interface ArcPointFt { x: number; y: number; z: number }

/** Segments per arc; every segment runs through the ordinary straight line-of-effect test. */
export const ARC_SAMPLE_SEGMENTS = 12;
/** A 300 ft trebuchet shot must not climb 75 ft; long-range engines clamp here. */
export const ARC_APEX_MAX_FT = 60;
/** The flattest arc: max range flies at 45°. */
export const ARC_MIN_ANGLE_DEG = 45;
/** Point blank lobs almost straight up. tan(90°) is unbounded, so the apex clamp does the work there. */
export const ARC_MAX_ANGLE_DEG = 90;

/** Launch angle (degrees) for a shot of horizontal length `distanceFt` from an ability of max range `rangeFt`. */
export function arcLaunchAngleDeg(distanceFt: number, rangeFt: number | null): number {
    if (!(distanceFt > 0)) return ARC_MAX_ANGLE_DEG;
    if (rangeFt === null || !Number.isFinite(rangeFt) || rangeFt <= 0) return ARC_MIN_ANGLE_DEG;
    const ratio = Math.min(1, Math.max(0, distanceFt / rangeFt));
    return ARC_MAX_ANGLE_DEG - (ARC_MAX_ANGLE_DEG - ARC_MIN_ANGLE_DEG) * ratio;
}

/** Apex height (ft) above the straight emitter→landing chord. */
export function arcApexHeightFt(distanceFt: number, rangeFt: number | null): number {
    if (!(distanceFt > 0)) return 0;
    const angleDeg = arcLaunchAngleDeg(distanceFt, rangeFt);
    const tan = angleDeg >= ARC_MAX_ANGLE_DEG - 1e-9 ? Number.POSITIVE_INFINITY : Math.tan(angleDeg * Math.PI / 180);
    return Math.min(ARC_APEX_MAX_FT, distanceFt * tan / 4);
}

/**
 * Every straight segment test excludes its own endpoints (a plane hit at t≈0 / t≈1 is "touching"), so a sample
 * that lands EXACTLY on a wall plane — 12 segments over a 10 ft shot puts one on x=10 — would slip through the
 * seam between two segments. Each tested segment therefore starts this fraction back along the PREVIOUS chord
 * (collinear with it, so the tested union stays exactly on the drawn polyline — Codex review 2026-09-15); the
 * first keeps the emitter and the last keeps the landing point exact (an overshoot below the landing point
 * would read as "through the floor").
 */
export const ARC_SEGMENT_OVERLAP = 0.05;

/** The [start, end] pairs to run through the straight line-of-effect test, overlapped per ARC_SEGMENT_OVERLAP. */
export function arcTestSegments(points: ArcPointFt[]): Array<[ArcPointFt, ArcPointFt]> {
    const segments: Array<[ArcPointFt, ArcPointFt]> = [];
    for (let i = 1; i < points.length; i += 1) {
        const a = points[i - 1]!;
        const b = points[i]!;
        const prev = i >= 2 ? points[i - 2]! : null;
        const start = prev === null ? a : {
            x: a.x - (a.x - prev.x) * ARC_SEGMENT_OVERLAP,
            y: a.y - (a.y - prev.y) * ARC_SEGMENT_OVERLAP,
            z: a.z - (a.z - prev.z) * ARC_SEGMENT_OVERLAP,
        };
        segments.push([start, b]);
    }
    return segments;
}

/**
 * Whole-flight parameter (0 = emitter, 1 = landing) of a hit at `hitT` along tested segment `index` (0-based),
 * honouring the overlap: segment i>0 spans (i − overlap)/N … (i + 1)/N of the flight.
 */
export function arcHitFlightT(index: number, hitT: number, segmentCount: number): number {
    const overlap = index === 0 ? 0 : ARC_SEGMENT_OVERLAP;
    return Math.max(0, Math.min(1, (index - overlap + (1 + overlap) * hitT) / segmentCount));
}

/**
 * The sampled parabola: ARC_SAMPLE_SEGMENTS + 1 points from the emitter (t=0) to the landing point (t=1).
 * Draw these; test arcTestSegments(points) for line of effect.
 */
export function arcSamplePoints(emitter: ArcPointFt, landing: ArcPointFt, rangeFt: number | null): ArcPointFt[] {
    const distanceFt = Math.hypot(landing.x - emitter.x, landing.y - emitter.y);
    const apex = arcApexHeightFt(distanceFt, rangeFt);
    const points: ArcPointFt[] = [];
    for (let i = 0; i <= ARC_SAMPLE_SEGMENTS; i += 1) {
        const t = i / ARC_SAMPLE_SEGMENTS;
        points.push({
            x: emitter.x + (landing.x - emitter.x) * t,
            y: emitter.y + (landing.y - emitter.y) * t,
            z: emitter.z + (landing.z - emitter.z) * t + 4 * apex * t * (1 - t),
        });
    }
    return points;
}
